import React, { RefObject } from 'react'
import { useRouter } from 'next/navigation'
import { FilterModel, GridApi, GridOptions, GridReadyEvent, PaginationChangedEvent, StateUpdatedEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { isEqual } from 'lodash'

import { NotificationProps, useNotification } from '@fsg/gui-bits'

import { useEngageServiceFactory } from '@app/hooks/useEngageServiceFactory'
import { EntityApiQueryParams } from '@app/types'
import { getServerSideGridData, SSRMSuccessCallback } from '@app/utils/getServerSideGridData'

import useExportAllData from './useExportAllData'
import { useGridStorage } from './useGridStorage'

type GridState = {
  count: number
  filterModel: FilterModel
  view: 'my' | 'all'
}

type Options<ResponseType> = {
  initialValues: GridState
  transformParams?: (args: EntityApiQueryParams) => EntityApiQueryParams
  onSuccess?: (args: SSRMSuccessCallback<ResponseType>) => void
}

export interface UseGridManagerReturn<ResponseType> {
  gridRef: RefObject<AgGridReact>
  count: number
  setCount: (count: number) => void
  onSuccess: (args: SSRMSuccessCallback<ResponseType>) => void
  gridStorage: ReturnType<typeof useGridStorage>
  onGridReady: (params: GridReadyEvent) => void
  onPaginationChanged: (params: PaginationChangedEvent) => void
  onStateUpdated: (params: StateUpdatedEvent) => void
  initialState: GridState
  filterModelChanged: boolean
  exportAllData: (ref: RefObject<AgGridReact>, fileName: string) => Promise<void>
  gridOptions: GridOptions
}

export function useGridManager<ResponseType>(
  tableId: string,
  service: ReturnType<typeof useEngageServiceFactory<any, ResponseType>>,
  options?: Options<ResponseType>,
): UseGridManagerReturn<ResponseType> {
  const { initialValues: propInitialValues, transformParams = (args) => args, onSuccess: _onSuccess } = options
  const initialValues = { view: 'all', ...propInitialValues }
  const router = useRouter()
  const notification = useNotification()
  const gridRef = React.useRef<AgGridReact>(null)
  const gridStorage = useGridStorage(tableId)
  const defaultConfig = React.useRef<any>(null)
  const initialFilter = React.useRef<any>(null)

  const [count, setCount] = React.useState<number>(() => {
    if (gridStorage.getLastCount() === undefined) return initialValues?.count ?? null
    return gridStorage.getLastCount()
  })

  if (defaultConfig.current === null) {
    defaultConfig.current = {
      defaultFilterModel: initialValues.filterModel,
    }
  }

  const updateUrl = React.useCallback(
    (params: { page?: number; filterModel: FilterModel }) => {
      const newUrl = new URL(window.location.toString())
      newUrl.searchParams.set('page', params.page !== undefined ? params.page.toString() : '1')
      newUrl.searchParams.set('filterModel', btoa(JSON.stringify(params.filterModel)))
      const urlString = newUrl.toString()
      window.history.pushState(window.history.state, '', urlString)
      gridStorage.setLastUrl(urlString)
      return newUrl
    },
    [router, gridStorage],
  )

  const getSearchParamsFromUrl = React.useCallback(
    (notify?: (notification: NotificationProps) => void) => {
      try {
        const currentUrl = new URL(window.location.href)
        const currentSearchParams = new URLSearchParams(currentUrl.search)

        const lastUrl = gridStorage.getLastUrl() !== undefined ? new URL(gridStorage.getLastUrl()) : undefined
        const lastSearchParams = lastUrl !== undefined ? new URLSearchParams(lastUrl?.search) : undefined

        const pageParam = currentSearchParams.get('page')
        let page = pageParam ? parseInt(pageParam, 10) : 1
        if (isNaN(page) || page === 0) page = 1

        const filterModelEncoded = currentSearchParams.get('filterModel') ?? lastSearchParams?.get('filterModel')
        let filterModel = {}
        if (filterModelEncoded) {
          try {
            filterModel = JSON.parse(atob(filterModelEncoded))
          } catch (decodeError) {
            throw new Error('Error parsing filters from URL. Falling back to defaults.')
          }
        }

        return { page, filterModel }
      } catch (error: any) {
        console.error('Error retrieving URL parameters:', error)
        if (notify) {
          notify({ type: 'warning', message: (error as Error).message })
        }
        return error as Error
      }
    },
    [gridStorage],
  )

  const handleSetCount = React.useCallback(
    (count: number) => {
      setCount(count)
      gridStorage.setLastCount(count)
    },
    [gridStorage],
  )

  const onSuccess = React.useCallback(
    (args: SSRMSuccessCallback<ResponseType>) => {
      const { filterModel, response } = args
      handleSetCount(response.totalCount)
      updateUrl({ page: 1, filterModel })
    },
    [handleSetCount, updateUrl],
  )

  const serverSideDatasource = getServerSideGridData<ResponseType>({
    service,
    onSuccess: _onSuccess ?? onSuccess,
    transformParams,
  })

  const resetFilterModelByView = React.useCallback((gridApi: GridApi<any>, filterModel: FilterModel) => {
    const urlView = new URL(window.location.href).searchParams.get('view')

    if (initialFilter.current === null) {
      initialFilter.current = 'initial filter set'
      gridApi.setFilterModel(filterModel)
    }
  }, [])

  const onGridReady = React.useCallback(
    (params: GridReadyEvent) => {
      const search = getSearchParamsFromUrl(notification.api.notify)
      let filterModelInUrl = {}
      if ('filterModel' in search) {
        filterModelInUrl = search.filterModel
      }
      const filterModel = { ...(initialValues.filterModel || {}), ...filterModelInUrl }

      updateUrl({ page: 1, filterModel })

      resetFilterModelByView(params.api, filterModel)

      params.api.setGridOption('serverSideDatasource', serverSideDatasource)
    },
    [getSearchParamsFromUrl, notification.api.notify, initialValues.filterModel, updateUrl, resetFilterModelByView, serverSideDatasource],
  )

  const onPaginationChanged = React.useCallback((params: PaginationChangedEvent) => {
    params.api.setGridOption('cacheBlockSize', params.api.paginationGetPageSize())
  }, [])

  const onStateUpdated = React.useCallback(
    (params: StateUpdatedEvent) => {
      gridStorage.setLastGridState(params.state)
    },
    [gridStorage],
  )

  const resetGrid = React.useCallback(
    (gridApi: GridApi<any>) => {
      let filterModelInUrl = {}
      const search = getSearchParamsFromUrl(notification.api.notify)

      if ('filterModel' in search) {
        filterModelInUrl = search.filterModel
      }
      const filterModel = { ...(initialValues.filterModel || {}), ...filterModelInUrl }
      updateUrl({ page: 1, filterModel })

      resetFilterModelByView(gridApi, filterModel)

      gridApi.setGridOption('serverSideDatasource', serverSideDatasource)
    },
    [getSearchParamsFromUrl, initialValues.filterModel, notification.api.notify, serverSideDatasource, resetFilterModelByView, updateUrl],
  )

  const exportAllData = useExportAllData<ResponseType>({ transformParams, service, resetGrid })

  const { defaultFilterModel } = defaultConfig.current
  const gridState = gridStorage.getLastGridState()
  const initialState = { ...gridState, defaultFilterModel: { ...defaultFilterModel } }

  const didFilterModelChange = () => {
    return gridState ? !isEqual(gridState.filter ? gridState.filter.filterModel : {}, initialState.defaultFilterModel) : false
  }

  const filterModelChanged = didFilterModelChange()

  return {
    gridRef,
    count,
    setCount,
    onSuccess,
    gridStorage,
    onGridReady,
    onPaginationChanged,
    onStateUpdated,
    initialState,
    filterModelChanged,
    exportAllData,
    gridOptions: { onGridReady, onPaginationChanged, onStateUpdated, initialState },
  }
}
